import React from 'react';

import type { UUID } from '@aline/common';
import { LAUNCHABLE_CRMS } from '@aline/common';
import { LinearProgress, Skeleton, Typography } from '@mui/material';
import { Share2 } from 'lucide-react';
import { ToggleSwitch } from 'src/components/generic';
import { ReconnectIntegrationButton } from 'src/components/send-for-signature/components/AddIntegration';
import { SettingsSection } from 'src/components/workflow/workflow-detail/side-panel/SettingsSection';
import { Apollo } from 'src/gql/generated';
import { INTEGRATION_DISPLAY_NAME } from 'src/helpers/integration-display-name';
import { useLogAndDispatchErrorSnack } from 'src/helpers/snackbars';
import {
  useCheckWorkflowAuthorization,
  useGetWorkflowSimple,
  useUserIntegrations,
} from 'src/hooks';
import { CrmIntegrationIcon } from 'src/images/integrations/CrmIntegrationIcon';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  caption: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    lineHeight: 1.4,
    marginTop: 2,
  },
  captionSkeleton: {
    borderRadius: 4,
    marginTop: 2,
  },
  controlSkeleton: {
    borderRadius: 10,
    flexShrink: 0,
  },
  copy: {
    flex: 1,
    minWidth: 0,
  },
  crmIcon: {
    display: 'block',
    height: 22,
    objectFit: 'contain',
    width: 22,
  },
  crmName: {
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.3,
  },
  logoTile: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    display: 'flex',
    flexShrink: 0,
    height: 36,
    justifyContent: 'center',
    width: 36,
  },
  plate: {
    alignItems: 'center',
    backgroundColor: 'rgba(20, 110, 232, 0.05)',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    display: 'flex',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25, 1.5),
  },
  progress: {
    height: 2,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  sectionGlyph: {
    color: theme.appColors.iconsType,
    flexShrink: 0,
  },
  sectionLabel: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    position: 'relative',
    width: '100%',
  },
}));

export function TemplateCrmConfig({ workflowId }: { workflowId: UUID }) {
  const { classes } = useStyles();
  const { allConnections, initialLoading } = useUserIntegrations({
    fetchPolicy: 'cache-and-network',
  });
  const { workflow } = useGetWorkflowSimple(workflowId);

  if (!workflow || workflow.variant !== Apollo.WorkflowVariant.Template) return null;

  const rows = LAUNCHABLE_CRMS.flatMap(integration => {
    const connection = allConnections.find(c => c.integration === integration);
    // A published CRM keeps its row after the connection goes away so publishing can still be turned off.
    if (!connection && !workflow.crmIntegrations.includes(integration)) return [];
    return [
      { integration, isConnected: connection?.state === Apollo.ApiDeckConnectionState.Callable },
    ];
  });

  if (!initialLoading && rows.length === 0) return null;

  return (
    <SettingsSection className={classes.wrapper}>
      {initialLoading && <LinearProgress className={classes.progress} />}
      <div className={classes.sectionLabel}>
        <Share2 className={classes.sectionGlyph} size={16} />
        <Typography className={classes.sectionTitle}>Publish to CRM</Typography>
      </div>
      {rows.map(({ integration, isConnected }) => (
        <CrmPublishRow
          key={integration}
          crmIntegrations={workflow.crmIntegrations}
          initialLoading={initialLoading}
          integration={integration}
          isConnected={isConnected}
          workflowId={workflowId}
        />
      ))}
    </SettingsSection>
  );
}

function CrmPublishRow({
  crmIntegrations,
  initialLoading,
  integration,
  isConnected,
  workflowId,
}: {
  crmIntegrations: Apollo.Integration[];
  initialLoading: boolean;
  integration: Apollo.Integration;
  isConnected: boolean;
  workflowId: UUID;
}) {
  const { classes } = useStyles();
  const isPublished = crmIntegrations.includes(integration);
  const providerName = INTEGRATION_DISPLAY_NAME[integration];
  const caption = isPublished
    ? `This template can be launched from ${providerName}.`
    : `This template is hidden from ${providerName} launch flows.`;

  return (
    <div className={classes.plate}>
      <div className={classes.logoTile}>
        <CrmIntegrationIcon className={classes.crmIcon} integration={integration} />
      </div>
      <div className={classes.copy}>
        <Typography className={classes.crmName}>{providerName}</Typography>
        <CrmPublishCaption
          caption={caption}
          initialLoading={initialLoading}
          isConnected={isConnected}
          isPublished={isPublished}
        />
      </div>
      <CrmPublishControl
        crmIntegrations={crmIntegrations}
        initialLoading={initialLoading}
        integration={integration}
        isConnected={isConnected}
        workflowId={workflowId}
      />
    </div>
  );
}

function CrmPublishCaption({
  caption,
  initialLoading,
  isConnected,
  isPublished,
}: {
  caption: string;
  initialLoading: boolean;
  isConnected: boolean;
  isPublished: boolean;
}) {
  const { classes } = useStyles();
  if (initialLoading)
    return (
      <Skeleton className={classes.captionSkeleton} height={12} variant="rounded" width={180} />
    );
  if (isConnected) return <Typography className={classes.caption}>{caption}</Typography>;
  if (isPublished)
    return (
      <Typography className={classes.caption}>
        Published, but the connection is gone. Reconnect or turn publishing off.
      </Typography>
    );

  return <Typography className={classes.caption}>Connect to publish this template</Typography>;
}

function CrmPublishControl({
  crmIntegrations,
  initialLoading,
  integration,
  isConnected,
  workflowId,
}: {
  crmIntegrations: Apollo.Integration[];
  initialLoading: boolean;
  integration: Apollo.Integration;
  isConnected: boolean;
  workflowId: UUID;
}) {
  const { classes } = useStyles();
  if (initialLoading)
    return (
      <Skeleton className={classes.controlSkeleton} height={20} variant="rounded" width={36} />
    );
  if (!isConnected && !crmIntegrations.includes(integration))
    return <ReconnectIntegrationButton integration={integration} size="small" />;

  return (
    <CrmAvailabilityToggle
      crmIntegrations={crmIntegrations}
      integration={integration}
      workflowId={workflowId}
    />
  );
}

function CrmAvailabilityToggle({
  crmIntegrations,
  integration,
  workflowId,
}: {
  crmIntegrations: Apollo.Integration[];
  integration: Apollo.Integration;
  workflowId: UUID;
}) {
  const [setWorkflowCrmPublish] = Apollo.useSetWorkflowCrmPublishMutation();
  const { canEditWorkflow } = useCheckWorkflowAuthorization(workflowId);
  const logAndDispatchError = useLogAndDispatchErrorSnack({
    message: 'Failed to update CRM availability.',
  });
  const isPublished = crmIntegrations.includes(integration);

  async function handleToggle(enabled: boolean) {
    const nextIntegrations = enabled
      ? [...new Set([...crmIntegrations, integration])]
      : crmIntegrations.filter(current => current !== integration);
    try {
      await setWorkflowCrmPublish({
        optimisticResponse: {
          __typename: 'Mutation',
          updateWorkflow: {
            __typename: 'WorkflowResponse',
            workflow: {
              __typename: 'Workflow',
              crmIntegrations: nextIntegrations,
              id: workflowId,
            },
          },
        },
        variables: {
          input: {
            props: { publishedCrmIntegrations: nextIntegrations },
            workflowId,
          },
        },
      });
    } catch (error) {
      logAndDispatchError(error instanceof Error ? error : new Error(String(error)));
    }
  }

  return (
    <ToggleSwitch
      disabled={!canEditWorkflow}
      toggle={() => {
        void handleToggle(!isPublished);
      }}
      toggled={isPublished}
    />
  );
}
